import { AxiosError } from 'axios';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { isDefined, isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { useTranslation } from 'react-i18next';
import { Box, CardHeading, makeStyles, Pagination, Paper } from '@dxb-web-ui-framework/design-system';
import React, { ChangeEvent, useCallback, useState, VFC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { TransactionDetailsDrawer } from '../TransactionDetails/TransactionDetailsDrawer';
import { renderTableGridActions, TableGrid } from '../../../../content-composer/src/components/Generic/TableGrid';
import {
  ConsolidatedTransaction,
  ConsolidatedTransactionDetails,
} from '../../domain/Transaction/entities/ConsolidatedTransaction';
import {
  useConsolidatedTransactionsProviderErrorState,
  useConsolidatedTransactionsProviderLoadingState,
  useConsolidatedTransactions,
} from '../../contexts/ConsolidatedTransactions/hooks';
import { ConsolidatedTransactionListFilters } from '../../contexts/ConsolidatedTransactions/types';
import { getRoutePath } from '../../../../content-composer/src/components/Routes/routePaths';
import { TableFilterDrawer } from '../../../../content-composer/src/components/Generic/TableGrid';
import { ErrorAlert } from '../../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import {
  useCheckAuthorization,
  useConsolidatedTransactionDetailsData,
  useConsolidatedTransactionDetailsProviderErrorState,
  useConsolidatedTransactionDetailsProviderLoadingState,
  useResetSelectedConsolidatedTransaction,
} from '../../contexts/ConsolidatedTransactionDetails/hooks';
import { SelectedFiltersChip } from '../../../../content-composer/src/components/Generic/SelectedFiltersChip';
import { useConsolidatedTransactionsGridConfig } from './useConsolidatedTransactionsGridConfig';
import {
  ORDER_DIRECTION_DEFAULT_VALUE,
  ORDER_STATUS_DEFAULT_VALUE,
  useConsolidatedTransactionsFiltersConfig,
} from './useConsolidatedTransactionsFiltersConfig';

const useStyles = makeStyles(() => ({
  transactionsHistoryTableWrapper: {
    '& th': {
      fontSize: '1em',
    },
    '& td': {
      fontSize: '0.9em',
    },
  },
}));

export type TransactionsTableProps = {
  customerId: string;
  onSelectTransaction: (transactionNo?: string) => void;
  totalItems?: number;
  currentPage?: number;
  isTransactionsFilterOpen: boolean;
  filters: Nullable<Partial<ConsolidatedTransactionListFilters>>;
  onToggleFilterDrawer: () => void;
  onPageChange?: (event: ChangeEvent<unknown>, page: number) => void;
  onChangeFilters: (newFilters: Partial<ConsolidatedTransactionListFilters>) => void;
  downloadComponent: React.FC;
};

const ConsolidatedTransactionsTable: VFC<TransactionsTableProps> = ({
  customerId,
  onSelectTransaction,
  totalItems,
  currentPage,
  onPageChange,
  onChangeFilters,
  filters,
  isTransactionsFilterOpen,
  onToggleFilterDrawer,
  downloadComponent,
}) => {
  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(false);
  const { t } = useTranslation();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const classes = useStyles();
  const filtersConfig = useConsolidatedTransactionsFiltersConfig();
  //consolidated transactions hooks
  const transactionsList = useConsolidatedTransactions();
  const checkAuthorization = useCheckAuthorization();
  const isLoadingTransactionList = useConsolidatedTransactionsProviderLoadingState(
    'isLoadingConsolidatedTransactionList',
  );
  const transactionListError = useConsolidatedTransactionsProviderErrorState('consolidatedTransactionListError');
  //consolidated transaction details hooks
  const consolidatedTransactionDetails = useConsolidatedTransactionDetailsData();
  const isLoadingConsolidatedTransactionDetails = useConsolidatedTransactionDetailsProviderLoadingState(
    'isLoadingConsolidatedTransactionDetails',
  );
  const isLoadingCheckAuthorization =
    useConsolidatedTransactionDetailsProviderLoadingState('isLoadingCheckAuthorization');
  const consolidatedTransactionDetailsError = useConsolidatedTransactionDetailsProviderErrorState(
    'consolidatedTransactionDetailsError',
  );

  const resetConsolidatedTransaction = useResetSelectedConsolidatedTransaction();

  const navigate = useNavigate();

  const onToggleDrawer = useCallback(
    (transactionNo: string) => {
      if (!isTransactionDetailsOpen) {
        onSelectTransaction(transactionNo);
      } else {
        resetConsolidatedTransaction();
      }
      setIsTransactionDetailsOpen(prev => !prev);
    },
    [onSelectTransaction, isTransactionDetailsOpen, setIsTransactionDetailsOpen, resetConsolidatedTransaction],
  );

  // TODO: we need different behaviour of the consolidated transactions page?
  const onSelectListItem = useCallback(
    (rowData: ConsolidatedTransaction) => {
      onSelectTransaction(rowData.transactionNo);
      navigate(
        getRoutePath({
          routeKey: 'transaction-details',
          customerId,
          transactionNo: rowData.transactionNo,
        }),
      );
    },
    [navigate, customerId, onSelectTransaction],
  );

  const tableGridConfig = useConsolidatedTransactionsGridConfig(
    onSelectListItem,
    onToggleDrawer,
    onToggleFilterDrawer,
    downloadComponent,
  );

  const renderedTableGridActions = renderTableGridActions(tableGridConfig.actions);

  const shouldShowTableActions = !isMobile && !isEmpty(tableGridConfig.actions);

  const onDeleteChipFilter = (newFilters: Nullable<Partial<ConsolidatedTransactionListFilters>>) => {
    onChangeFilters(newFilters!);
  };

  const modifyDataDrawer = (newFilters: Partial<GenericObject>) => {
    if (newFilters.transactionDate ?? false) {
      newFilters.fromTrxDate = newFilters.transactionDate[0];
      newFilters.toTrxDate = newFilters.transactionDate[1];
    }

    if (newFilters.valueDate ?? false) {
      newFilters.valueDateFrom = newFilters.valueDate[0];
      newFilters.valueDateTo = newFilters.valueDate[1];
    }

    if (!newFilters.fromAmount ?? true) {
      delete newFilters.fromAmount;
    }

    if (!newFilters.toAmount ?? true) {
      delete newFilters.toAmount;
    }

    if (newFilters?.orderStatus === ORDER_STATUS_DEFAULT_VALUE) {
      delete newFilters.orderStatus;
      delete newFilters.orderStatusDescription;
    }

    if (newFilters?.orderDirection === ORDER_DIRECTION_DEFAULT_VALUE) {
      delete newFilters.orderDirection;
    }

    delete newFilters.transactionDate;
    delete newFilters.valueDate;

    onChangeFilters(newFilters!);
  };

  const totalPages = totalItems ? Math.ceil(totalItems / (filters?.pageSize ?? 1)) : undefined;
  const shouldShowPagination = isDefined(totalPages) && currentPage && onPageChange;

  return (
    <>
      <Box marginTop={isMobile ? 6 : 5} width='100%' />

      <Box pl={isMobile ? 3 : 0}>
        <CardHeading
          heading={t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.Heading')}
          hideOnMobile={false}
        />
      </Box>

      <Paper elevation={isMobile ? 0 : 1}>
        <Box display={'flex'} justifyContent='space-between' alignItems={'center'} paddingX={3} paddingY={2}>
          <Box>
            <SelectedFiltersChip
              filters={filters!}
              filtersConfig={filtersConfig}
              onChangeFilters={onDeleteChipFilter}
              shouldResetFromAndToDatesToInitialAndDisableChip={true}
            />
          </Box>
          {shouldShowTableActions && <Box display={'flex'}>{renderedTableGridActions}</Box>}
        </Box>
        {transactionListError && !isEmpty(transactionListError) && (
          <ErrorAlert message={transactionListError.message} hasSpacing={true} />
        )}
        <Box className={classes.transactionsHistoryTableWrapper}>
          <TableGrid
            gridOptions={tableGridConfig.gridOptions}
            columnDefs={tableGridConfig.columnDefs}
            rowData={transactionsList}
            hasSkeleton={isLoadingTransactionList}
            mobileListConfig={tableGridConfig.mobileListConfig}>
            {shouldShowPagination && (
              <Pagination
                color='primary'
                count={totalPages}
                page={currentPage}
                siblingCount={isMobile ? 0 : undefined}
                onChange={onPageChange}
                disabled={false}
              />
            )}
          </TableGrid>
        </Box>
      </Paper>

      {!isMobile && isTransactionDetailsOpen && (
        <TransactionDetailsDrawer
          transactionDetails={consolidatedTransactionDetails}
          isLoading={isLoadingConsolidatedTransactionDetails || isLoadingCheckAuthorization}
          error={
            consolidatedTransactionDetailsError as
              | AxiosError<APIGatewayResponse<ConsolidatedTransactionDetails>, any>
              | undefined
          }
          isOpen={isTransactionDetailsOpen}
          onToggleDrawer={onToggleDrawer}
          shouldFetchDealerRates={true}
          lastAuthorizer={checkAuthorization?.lastAuthorizer ?? false}
          needMandate={checkAuthorization?.needMandate ?? true}
        />
      )}

      <TableFilterDrawer
        formConfig={filtersConfig}
        isOpen={isTransactionsFilterOpen}
        onToggleDrawer={onToggleFilterDrawer}
        title={t('consolidatedTransactionsPage.TableFilterDrawer.Title')}
        applyButtonLabel={t('consolidatedTransactionsPage.TableFilterDrawer.BtnLabel')}
        onApplyDrawer={modifyDataDrawer}
        filters={{
          ...filters,
          orderStatus: filters?.orderStatus ?? ORDER_STATUS_DEFAULT_VALUE,
          transactionDate: [
            filters?.fromTrxDate ? new Date(filters.fromTrxDate) : null,
            filters?.toTrxDate ? new Date(filters.toTrxDate) : null,
          ],
          valueDate: [
            filters?.valueDateFrom ? new Date(filters.valueDateFrom) : null,
            filters?.valueDateTo ? new Date(filters.valueDateTo) : null,
          ],
          orderDirection: filters?.orderDirection ?? ORDER_DIRECTION_DEFAULT_VALUE,
        }}
      />
    </>
  );
};

export { ConsolidatedTransactionsTable };
